import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi } from '../api/auth';
import { runBacktest } from '../api/backtest';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts';
import Sidebar from '../components/Sidebar';
import CollectingOverlay from '../components/CollectingOverlay';

function Backtest() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  
  const [config, setConfig] = useState({
    start_date: new Date().getFullYear() + '-01-01',
    end_date: new Date().toISOString().split('T')[0],
    initial_capital: 10000000,
    splits: 20,
    stop_loss: 8,
    take_profit: 24,
    use_ma20_exit: true,
    use_kospi_filter: false,
    use_fee: false,
    buy_fee: 0.015,
    sell_fee: 0.20,
    use_volume_filter: true,
    use_mtt_filter: false,
    use_surge_filter: false,
    max_surge_rate: 25,
    use_fresh_breakout: false,
    lookback_days: 3,
    min_trading_value: 30,
    use_rs_filter: false,
    min_sector_rs: 75,
    min_stock_rs: 80,
    use_op_filter: false,
    min_op_growth: 15
  });

  const [capitalDisplay, setCapitalDisplay] = useState('10,000,000');

  useEffect(() => {
    const init = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const handleLogout = () => {
    logoutApi();
    navigate('/login');
  };

  const formatNumberWithComma = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleCapitalChange = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/^0+/, '');
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? 0 : parseInt(raw, 10);
      setConfig({ ...config, initial_capital: num });
      setCapitalDisplay(raw === '' ? '' : formatNumberWithComma(num));
    }
  };

  const handleCapitalBlur = () => {
    if (capitalDisplay === '' || config.initial_capital === 0) {
      setConfig({ ...config, initial_capital: 0 });
      setCapitalDisplay('0');
    }
  };

  const handleNumberChange = (field, value, isFloat = false) => {
    let cleaned = value.replace(/^0+/, '') || '0';
    if (isFloat) {
      cleaned = value.replace(/^0+(?=\d)/, '');
      const num = parseFloat(cleaned);
      if (!isNaN(num) || cleaned === '' || cleaned === '-') {
        setConfig({ ...config, [field]: cleaned === '' ? 0 : num });
      }
    } else {
      const num = parseInt(cleaned, 10);
      if (!isNaN(num)) {
        setConfig({ ...config, [field]: num });
      }
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setProgress(0);
    setProgressText('준비 중...');
    
    const steps = [
      { pct: 5, text: '종목 리스트 로딩 중...' },
      { pct: 15, text: '주가 데이터 로딩 중...' },
      { pct: 40, text: '52주 최고가 계산 중...' },
      { pct: 60, text: '매매 시뮬레이션 중...' },
      { pct: 80, text: '수익률 계산 중...' },
      { pct: 95, text: '결과 정리 중...' },
    ];
    
    let stepIndex = 0;
    const interval = setInterval(() => {
      if (stepIndex < steps.length) {
        setProgress(steps[stepIndex].pct);
        setProgressText(steps[stepIndex].text);
        stepIndex++;
      }
    }, 10000);
    
    try {
      const res = await runBacktest(config);
      clearInterval(interval);
      setProgress(100);
      setProgressText('완료!');
      
      setTimeout(() => {
        setResult(res);
        setRunning(false);
        setProgress(0);
        setActiveTab('summary');
      }, 500);
    } catch (err) {
      clearInterval(interval);
      setRunning(false);
      setProgress(0);
      alert(err.response?.data?.detail || '백테스트 실행 실패');
    }
  };

  const formatMoney = (amount) => {
    if (!amount) return '₩0';
    return '₩' + Math.round(amount).toLocaleString('ko-KR');
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    return (value >= 0 ? '+' : '') + value.toFixed(2) + '%';
  };

  const getReturnData = () => {
    if (!result?.portfolio_values) return [];
    const initial = config.initial_capital;
    return result.portfolio_values.map(p => ({
      date: p.date,
      return: ((p.value - initial) / initial) * 100,
      value: p.value
    }));
  };

  const getDrawdownData = () => {
    if (!result?.portfolio_values) return [];
    let peak = result.portfolio_values[0]?.value || 0;
    return result.portfolio_values.map(p => {
      if (p.value > peak) peak = p.value;
      const drawdown = ((p.value - peak) / peak) * 100;
      return { date: p.date, drawdown: drawdown };
    });
  };

  const getMonthlyReturns = () => {
    if (!result?.portfolio_values || result.portfolio_values.length === 0) return { years: [], data: {} };
    
    const monthlyData = {};
    result.portfolio_values.forEach(p => {
      const date = new Date(p.date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const key = `${year}-${month}`;
      
      if (!monthlyData[key]) {
        monthlyData[key] = { year, month, first: p.value, last: p.value };
      } else {
        monthlyData[key].last = p.value;
      }
    });
    
    const yearlyReturns = {};
    let prevMonthEnd = config.initial_capital;
    
    const sortedKeys = Object.keys(monthlyData).sort((a, b) => {
      const [ay, am] = a.split('-').map(Number);
      const [by, bm] = b.split('-').map(Number);
      return ay !== by ? ay - by : am - bm;
    });
    
    sortedKeys.forEach(key => {
      const { year, month, first, last } = monthlyData[key];
      
      if (!yearlyReturns[year]) {
        yearlyReturns[year] = { months: {}, yearStart: prevMonthEnd };
      }
      
      const monthReturn = ((last - prevMonthEnd) / prevMonthEnd) * 100;
      yearlyReturns[year].months[month] = monthReturn;
      yearlyReturns[year].yearEnd = last;
      
      prevMonthEnd = last;
    });
    
    Object.keys(yearlyReturns).forEach(year => {
      const yr = yearlyReturns[year];
      yr.yearReturn = ((yr.yearEnd - yr.yearStart) / yr.yearStart) * 100;
    });
    
    const years = Object.keys(yearlyReturns).map(Number).sort((a, b) => a - b);
    
    return { years, data: yearlyReturns };
  };

  const getCAGR = () => {
    if (!result?.summary) return 0;
    const years = (new Date(config.end_date) - new Date(config.start_date)) / (365 * 24 * 60 * 60 * 1000);
    if (years < 1) return result.summary.total_return.toFixed(2);  // 1년 미만이면 누적수익률
    const totalReturn = result.summary.final_value / result.summary.initial_capital;
    return ((Math.pow(totalReturn, 1/years) - 1) * 100).toFixed(2);
  };

  const getProfitLossRatio = () => {
    if (!result?.trades) return 0;
    const wins = result.trades.filter(t => t.action === 'SELL' && t.profit > 0);
    const losses = result.trades.filter(t => t.action === 'SELL' && t.profit < 0);
    const avgWin = wins.length > 0 ? wins.reduce((a, b) => a + b.profit, 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((a, b) => a + b.profit, 0) / losses.length) : 1;
    return avgLoss > 0 ? (avgWin / avgLoss).toFixed(2) : '∞';
  };

  const getCellStyle = (value) => {
    if (value === null || value === undefined) return 'bg-gray-50 text-gray-400';
    if (value > 0) return 'bg-green-50 text-green-600';
    if (value < 0) return 'bg-red-50 text-red-600';
    return 'bg-gray-50 text-gray-500';
  };

  if (loading) {
    return (
      <CollectingOverlay>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CollectingOverlay>
    );
  }

  const monthlyReturns = getMonthlyReturns();
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <CollectingOverlay>
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-6xl mx-auto overflow-auto">
        <header className="flex justify-between items-center mb-3 md:mb-4">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-base md:text-2xl font-bold text-gray-800">백테스트</h1>
              <p className="text-xs text-gray-500">{config.start_date} ~ {config.end_date}</p>
            </div>
            {result && (
              <div className="flex space-x-1 ml-2">
                <button onClick={() => setActiveTab('summary')} className={`px-2 py-1 text-xs font-medium rounded transition ${activeTab === 'summary' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  성과요약
                </button>
                <button onClick={() => setActiveTab('analysis')} className={`px-2 py-1 text-xs font-medium rounded transition ${activeTab === 'analysis' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  성과분석
                </button>
                <button onClick={() => setActiveTab('trades')} className={`px-2 py-1 text-xs font-medium rounded transition ${activeTab === 'trades' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  매매내역
                </button>
                <button onClick={() => setActiveTab('monthly')} className={`px-2 py-1 text-xs font-medium rounded transition ${activeTab === 'monthly' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  월별수익
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          {/* 설정 패널 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <h3 className="text-sm text-gray-500 font-medium mb-2">전략 설정</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">시작일</label>
                  <input type="date" value={config.start_date} style={{letterSpacing: "-1px", fontSize: "13px"}} onChange={(e) => setConfig({...config, start_date: e.target.value})} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={running} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">종료일</label>
                  <input type="date" value={config.end_date} style={{letterSpacing: "-1px", fontSize: "13px"}} onChange={(e) => setConfig({...config, end_date: e.target.value})} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={running} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">초기 자본금</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={capitalDisplay} 
                    onChange={handleCapitalChange}
                    onBlur={handleCapitalBlur}
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-right" 
                    disabled={running} 
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">원</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">분할 매수</label>
                  <input 
                    type="text" 
                    value={config.splits} 
                    onChange={(e) => handleNumberChange('splits', e.target.value)} 
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-center" 
                    disabled={running} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">손절 (-%)</label>
                  <input 
                    type="text" 
                    value={config.stop_loss} 
                    onChange={(e) => handleNumberChange('stop_loss', e.target.value, true)} 
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-center" 
                    disabled={running} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">익절 (%)</label>
                  <input 
                    type="text" 
                    value={config.take_profit} 
                    onChange={(e) => handleNumberChange('take_profit', e.target.value, true)} 
                    placeholder="0=OFF" 
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-center" 
                    disabled={running} 
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">익절 0 = 비활성 (20일선만 사용)</p>
              
              <div className="flex items-center justify-between py-1">
                <span className="text-xs text-gray-700">20일 이평선 익절</span>
                <button onClick={() => !running && setConfig({...config, use_ma20_exit: !config.use_ma20_exit})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_ma20_exit ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_ma20_exit ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs text-gray-700">코스피 60일선 필터</span>
                <button onClick={() => !running && setConfig({...config, use_kospi_filter: !config.use_kospi_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_kospi_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_kospi_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <p className="text-xs text-gray-400 -mt-2">코스피가 60일 이평선 위일 때만 매수</p>
              
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs text-gray-700">거래대금 필터</span>
                <button onClick={() => !running && setConfig({...config, use_volume_filter: !config.use_volume_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_volume_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_volume_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              {config.use_volume_filter && (
                <div className="bg-gray-50 p-3 rounded-lg -mt-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">최소 거래대금 (억원)</label>
                  <input type="number" value={config.min_trading_value} onChange={(e) => setConfig({...config, min_trading_value: e.target.value === '' ? '' : Number(e.target.value)})} step="1" min={0} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={running} />
                  <p className="text-xs text-gray-400 mt-1">입력값 × 1억원 이상 종목만 매수</p>
                </div>
              )}
              
              {/* MTT 필터 (Minervini Trend Template) */}
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <div>
                  <span className="text-xs text-gray-700">MTT 필터</span>
                  <p className="text-xs text-gray-400">Minervini Trend Template</p>
                </div>
                <button onClick={() => !running && setConfig({...config, use_mtt_filter: !config.use_mtt_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_mtt_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_mtt_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_mtt_filter && (
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-500 space-y-0.5">
                  <p>1. 종가 {'>'} 150일선</p>
                  <p>2. 종가 {'>'} 200일선</p>
                  <p>3. 150일선 {'>'} 200일선</p>
                  <p>4. 200일선 22일 이상 상승 중</p>
                  <p>5. 50일선 {'>'} 150일선</p>
                  <p>6. 종가 {'>'} 50일선</p>
                </div>
              )}

              {/* 급등 종목 제외 필터 */}
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <div>
                  <span className="text-xs text-gray-700">급등 종목 제외</span>
                  <p className="text-xs text-gray-400">당일 상승률 기준</p>
                </div>
                <button onClick={() => !running && setConfig({...config, use_surge_filter: !config.use_surge_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_surge_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_surge_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_surge_filter && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-xs font-medium text-gray-500 mb-1">당일 상승률 (%) 이상 제외</label>
                  <input type="number" value={config.max_surge_rate} onChange={(e) => setConfig({...config, max_surge_rate: parseFloat(e.target.value) || 25})} disabled={running} className="w-full px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
              )}
              
              {/* 신선한 돌파 필터 */}
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <div>
                  <span className="text-xs text-gray-700">신선한 돌파만</span>
                  <p className="text-xs text-gray-400">이전 n일 내 미돌파</p>
                </div>
                <button onClick={() => !running && setConfig({...config, use_fresh_breakout: !config.use_fresh_breakout})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_fresh_breakout ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_fresh_breakout ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_fresh_breakout && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-xs font-medium text-gray-500 mb-1">이전 며칠 확인</label>
                  <input type="number" value={config.lookback_days} onChange={(e) => setConfig({...config, lookback_days: parseInt(e.target.value) || 3})} disabled={running} min="1" max="20" className="w-full px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  <p className="text-xs text-gray-400 mt-1">이전 n일 동안 돌파 없었을 때만 진입</p>
                </div>
              )}

              {/* RS 필터 */}
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <div>
                  <span className="text-xs text-gray-700">RS 필터</span>
                  <p className="text-xs text-gray-400">섹터/종목 RS Rating</p>
                </div>
                <button onClick={() => !running && setConfig({...config, use_rs_filter: !config.use_rs_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_rs_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_rs_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_rs_filter && (
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">최소 섹터 RS</label>
                    <input type="number" value={config.min_sector_rs} onChange={(e) => setConfig({...config, min_sector_rs: parseInt(e.target.value) || 75})} disabled={running} min="1" max="99" className="w-full px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">최소 종목 RS</label>
                    <input type="number" value={config.min_stock_rs} onChange={(e) => setConfig({...config, min_stock_rs: parseInt(e.target.value) || 80})} disabled={running} min="1" max="99" className="w-full px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <p className="text-xs text-gray-400">섹터RS {'>='} {config.min_sector_rs}, 종목RS {'>='} {config.min_stock_rs}</p>
                </div>
              )}

              {/* 영업이익 필터 */}
              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <div>
                  <span className="text-xs text-gray-700">영업이익 필터</span>
                  <p className="text-xs text-gray-400">흑자 + YoY 성장</p>
                </div>
                <button onClick={() => !running && setConfig({...config, use_op_filter: !config.use_op_filter})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_op_filter ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_op_filter ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_op_filter && (
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">영업이익 YoY ≥ (%)</label>
                    <input type="number" value={config.min_op_growth} onChange={(e) => setConfig({...config, min_op_growth: parseFloat(e.target.value) || 15})} disabled={running} min="0" max="100" className="w-full px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <p className="text-xs text-gray-400">영업이익 흑자 + 전년 동기 대비 +{config.min_op_growth}% 이상</p>
                </div>
              )}

              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs text-gray-700">수수료 적용</span>
                <button onClick={() => !running && setConfig({...config, use_fee: !config.use_fee})} disabled={running} className={`relative w-8 h-4 rounded-full transition-colors duration-300 ${config.use_fee ? 'bg-primary-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-md transition-transform duration-300 ${config.use_fee ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.use_fee && (
                <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">매수 수수료 (%)</label>
                    <input type="number" value={config.buy_fee} onChange={(e) => setConfig({...config, buy_fee: Number(e.target.value)})} step="0.001" min={0} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={running} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">매도 수수료 (%)</label>
                    <input type="number" value={config.sell_fee} onChange={(e) => setConfig({...config, sell_fee: Number(e.target.value)})} step="0.01" min={0} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={running} />
                  </div>
                  <p className="col-span-2 text-xs text-gray-400">매도 = 수수료 + 세금 (기본 0.20%)</p>
                </div>
              )}
              
              <button onClick={handleRun} disabled={running} className="w-full bg-primary-500 text-white py-3 rounded-xl font-medium hover:bg-primary-600 transition disabled:opacity-50">
                {running ? '실행 중...' : '백테스트 실행'}
              </button>
            </div>
          </div>

          {/* 결과 패널 */}
          <div className="lg:col-span-3 space-y-2">
            {running && (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <div className="animate-spin w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-sm text-gray-600 font-medium">백테스트 실행 중...</p>
                <p className="text-xs text-gray-400 mt-1 mb-3">{progressText}</p>
                <div className="max-w-xs mx-auto">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                    <span>진행률</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-primary-500 h-1.5 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>
            )}

            {result && activeTab === 'summary' && (
              <>
                {/* 자산현황 상단 카드 */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div className="md:col-span-2 bg-white rounded-lg shadow-sm px-3 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">초기자산</p>
                        <p className="text-sm font-bold text-gray-800 tabular-nums">{formatMoney(result.summary.initial_capital)}</p>
                      </div>
                      <div className="text-lg text-gray-300">→</div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">최종자산</p>
                        <p className={`text-sm font-bold tabular-nums ${result.summary.total_return >= 0 ? 'text-red-500' : 'text-blue-500'}`}>{formatMoney(result.summary.final_value)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">샤프 지수</span>
                    <span className="text-sm font-bold text-gray-800">{result.summary.sharpe_ratio}</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">CAGR</span>
                    <span className={`text-sm font-bold tabular-nums ${getCAGR() >= 0 ? 'text-red-500' : 'text-blue-500'}`}>{formatPercent(parseFloat(getCAGR()))}</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">누적수익률</span>
                    <span className={`text-sm font-bold tabular-nums ${result.summary.total_return >= 0 ? 'text-red-500' : 'text-blue-500'}`}>{formatPercent(result.summary.total_return)}</span>
                  </div>
                </div>

                {/* 추가 지표 */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">손익</span>
                    <span className={`text-sm font-bold tabular-nums ${result.summary.total_return >= 0 ? 'text-red-500' : 'text-blue-500'}`}>{formatMoney(result.summary.final_value - result.summary.initial_capital)}</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">MDD</span>
                    <span className="text-sm font-bold text-blue-500 tabular-nums">-{result.summary.max_drawdown}%</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">승률</span>
                    <span className="text-sm font-bold text-gray-800 tabular-nums">{result.summary.win_rate}%</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">손익비</span>
                    <span className="text-sm font-bold text-gray-800 tabular-nums">{getProfitLossRatio()}</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">매매횟수</span>
                    <span className="text-sm font-bold text-gray-800">{result.summary.total_trades}회</span>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">수수료</span>
                    <span className="text-sm font-bold text-gray-800 tabular-nums">{formatMoney(result.summary.total_fee || 0)}</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">누적 수익률 추이</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getReturnData()}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(0, 7)} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
                        <Tooltip formatter={(v) => [`${v.toFixed(2)}%`, '수익률']} labelFormatter={(l) => l} />
                        <ReferenceLine y={0} stroke="#ddd" />
                        <Line type="monotone" dataKey="return" stroke="#ef4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">고점 대비 하락폭 (Drawdown)</h3>
                  <div className="h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={getDrawdownData()}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(0, 7)} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={['auto', 0]} />
                        <Tooltip formatter={(v) => [`${v.toFixed(2)}%`, '하락폭']} />
                        <ReferenceLine y={0} stroke="#ddd" />
                        <Area type="monotone" dataKey="drawdown" stroke="#3b82f6" fill="#93c5fd" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {result && activeTab === 'analysis' && (
              <div className="space-y-2">
                {/* 거래 횟수 */}
                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">거래 횟수</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">총거래</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.sell_count}회</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">수익거래</span>
                      <span className="text-sm font-bold text-red-500">{result.summary.wins}회</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">손실거래</span>
                      <span className="text-sm font-bold text-blue-500">{result.summary.losses}회</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">승률</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.win_rate}%</span>
                    </div>
                  </div>
                </div>

                {/* 손익 분석 */}
                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">손익 분석</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균손익</span>
                      <span className={`text-sm font-bold tabular-nums ${(result.summary.avg_profit || 0) >= 0 ? 'text-red-500' : 'text-blue-500'}`}>{formatMoney(result.summary.avg_profit || 0)}</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균손익비</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.profit_loss_ratio || 0}:1</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">Profit Factor</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.profit_factor || 0}</span>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">MDD</span>
                      <span className="text-sm font-bold text-blue-500">-{result.summary.max_drawdown}%</span>
                    </div>
                  </div>
                </div>

                {/* 수익 거래 */}
                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">수익 거래</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="px-3 py-2 bg-red-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">최대수익</span>
                      <span className="text-sm font-bold text-red-500 tabular-nums">+{formatMoney(result.summary.max_profit || 0)}</span>
                    </div>
                    <div className="px-3 py-2 bg-red-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균수익</span>
                      <span className="text-sm font-bold text-red-500 tabular-nums">+{formatMoney(result.summary.avg_win || 0)}</span>
                    </div>
                    <div className="px-3 py-2 bg-red-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">최대연승</span>
                      <span className="text-sm font-bold text-red-500">{result.summary.max_win_streak || 0}연승</span>
                    </div>
                    <div className="px-3 py-2 bg-red-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균 보유일</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.avg_win_days || 0}일</span>
                    </div>
                  </div>
                </div>

                {/* 손실 거래 */}
                <div className="bg-white rounded-xl shadow-sm p-3">
                  <h3 className="text-sm text-gray-500 font-medium mb-2">손실 거래</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="px-3 py-2 bg-blue-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">최대손실</span>
                      <span className="text-sm font-bold text-blue-500 tabular-nums">{formatMoney(result.summary.max_loss || 0)}</span>
                    </div>
                    <div className="px-3 py-2 bg-blue-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균손실</span>
                      <span className="text-sm font-bold text-blue-500 tabular-nums">{formatMoney(result.summary.avg_loss || 0)}</span>
                    </div>
                    <div className="px-3 py-2 bg-blue-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">최대연패</span>
                      <span className="text-sm font-bold text-blue-500">{result.summary.max_loss_streak || 0}연패</span>
                    </div>
                    <div className="px-3 py-2 bg-blue-50 rounded-lg flex justify-between items-center">
                      <span className="text-xs text-gray-500">평균 보유일</span>
                      <span className="text-sm font-bold text-gray-800">{result.summary.avg_loss_days || 0}일</span>
                    </div>
                  </div>
                </div>

                {/* 총 수익/손실 */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white rounded-xl shadow-sm p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500 font-medium">총 수익</span>
                      <span className="text-sm font-bold text-red-500 tabular-nums">+{formatMoney(result.summary.total_profit || 0)}</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500 font-medium">총 손실</span>
                      <span className="text-sm font-bold text-blue-500 tabular-nums">-{formatMoney(result.summary.total_loss || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {result && activeTab === 'trades' && (
              <div className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm text-gray-500 font-medium">매매 내역</h3>
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">{result.trades?.length || 0}건</span>
                </div>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">날짜</th>
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-center py-2 px-2 text-xs font-medium text-gray-500">구분</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">가격</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">수량</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">금액</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">손익</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">수익률</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.trades && result.trades.slice().reverse().map((trade, idx) => (
                        <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-2 text-xs text-gray-600">{trade.date}</td>
                          <td className="py-2 px-2">
                            <p className="text-xs font-medium text-gray-800">{trade.name}</p>
                            <p className="text-xs text-gray-400">{trade.code}</p>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${trade.action === 'BUY' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                                {trade.action === 'BUY' ? '매수' : '매도'}
                              </span>
                              <span className="text-xs text-gray-400">{trade.position_count}/20</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-xs text-right text-gray-800 tabular-nums">{formatMoney(trade.price)}</td>
                          <td className="py-2 px-2 text-xs text-right text-gray-800">{trade.qty}주</td>
                          <td className="py-2 px-2 text-xs text-right text-gray-800 tabular-nums">{formatMoney(trade.amount)}</td>
                          <td className={`py-2 px-2 text-xs text-right font-medium tabular-nums ${trade.profit > 0 ? 'text-red-500' : trade.profit < 0 ? 'text-blue-500' : 'text-gray-400'}`}>
                            {trade.action === 'SELL' ? formatMoney(trade.profit) : '-'}
                          </td>
                          <td className={`py-2 px-2 text-xs text-right font-medium tabular-nums ${trade.profit > 0 ? 'text-red-500' : trade.profit < 0 ? 'text-blue-500' : 'text-gray-400'}`}>
                            {trade.action === 'SELL' ? `${trade.profit_rate}%` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result && activeTab === 'monthly' && (
              <div className="bg-white rounded-xl shadow-sm p-3">
                <h3 className="text-sm text-gray-500 font-medium mb-2">월별 수익률 (%)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="py-2 px-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-tl-lg">년도</th>
                        {months.map(m => (
                          <th key={m} className="py-2 px-1 text-xs font-semibold text-gray-600 bg-gray-100 text-center">{m}월</th>
                        ))}
                        <th className="py-2 px-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-tr-lg text-center">합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReturns.years.map((year, yi) => (
                        <tr key={year} className={yi % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <td className="py-2 px-1 text-xs font-semibold text-gray-700 text-center">{year}</td>
                          {months.map(m => {
                            const val = monthlyReturns.data[year]?.months[m];
                            return (
                              <td key={m} className={`py-2 px-1 text-xs text-center font-medium rounded tabular-nums ${getCellStyle(val)}`}>
                                {val !== undefined ? val.toFixed(2) : '-'}
                              </td>
                            );
                          })}
                          <td className={`py-2 px-1 text-xs text-center font-bold rounded tabular-nums ${getCellStyle(monthlyReturns.data[year]?.yearReturn)}`}>
                            {monthlyReturns.data[year]?.yearReturn !== undefined ? monthlyReturns.data[year].yearReturn.toFixed(2) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-50 rounded"></span> 수익</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-50 rounded"></span> 손실</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-50 rounded"></span> 없음</span>
                </div>
              </div>
            )}

            {!result && !running && (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-sm text-gray-500">전략 설정 후 백테스트를 실행하세요</p>
                <p className="text-xs text-gray-400 mt-1">DB에서 데이터를 로드하므로 빠르게 실행됩니다</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
    </CollectingOverlay>
  );
}

export default Backtest;
